import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link2, Search, X } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { askReason } from "./ApprovalLock";

const ctl = "h-9 px-2 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const candLabel = (c) => `${c.invoice_no} · ${c.date} · ${c.party_name}${c.vehicle_number ? ` · ${c.vehicle_number}` : ""} · ${inr(c.amount)}${c.challan_number ? ` · challan ${c.challan_number}` : ""}`;

const BillRow = ({ b, cands, byId, canWrite, onLink }) => {
  const [pick, setPick] = useState(b.suggestions[0] || "");
  const sugg = b.suggestions.map((id) => byId[id]).filter(Boolean);
  const others = cands.filter((c) => !b.suggestions.includes(c.id));
  return (
    <div data-testid={`finder-bill-${b.bill_no}`} className="grid grid-cols-1 lg:grid-cols-[120px_100px_130px_110px_minmax(0,1fr)_90px] gap-3 px-4 py-3 items-center text-sm">
      <span className="num font-semibold">{b.bill_no}</span>
      <span className="num text-xs">{b.date}</span>
      <span className="num text-xs">{b.vehicle_number || "—"}</span>
      <span className="num font-semibold lg:text-right">{inr(b.amount)}</span>
      <div className="min-w-0">
        <select data-testid={`finder-pick-${b.bill_no}`} className={`${ctl} w-full`} value={pick} onChange={(e) => setPick(e.target.value)} disabled={!canWrite}>
          <option value="">Choose Tata Motors invoice…</option>
          {sugg.length > 0 && <optgroup label="Likely matches">{sugg.map((c) => <option key={c.id} value={c.id}>{candLabel(c)}</option>)}</optgroup>}
          <optgroup label="Other invoices without this challan">{others.map((c) => <option key={c.id} value={c.id}>{candLabel(c)}</option>)}</optgroup>
        </select>
        {sugg.length > 0 && <div data-testid={`finder-suggested-${b.bill_no}`} className="text-[11px] text-[hsl(var(--positive))] mt-1">Suggested by vehicle / amount / date</div>}
      </div>
      {canWrite && (
        <button data-testid={`finder-link-${b.bill_no}`} disabled={!pick} onClick={() => onLink(b, byId[pick])}
          className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium flex items-center justify-center gap-1.5 hover:bg-primary/90 transition-colors disabled:opacity-40">
          <Link2 size={13} /> Link
        </button>
      )}
    </div>
  );
};

export const UnlinkedBillFinder = ({ onClose, onLinked, canWrite }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(async () => {
    try { setData((await api.get("/store-sync/unlinked-finder")).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const byId = useMemo(() => Object.fromEntries((data?.candidates || []).map((c) => [c.id, c])), [data]);
  const bills = useMemo(() => (data?.bills || []).filter((b) => !q.trim() || `${b.bill_no} ${b.vehicle_number}`.toLowerCase().includes(q.trim().toLowerCase())), [data, q]);

  const link = async (b, inv) => {
    if (!inv) return;
    if (inv.challan_number && !window.confirm(`${inv.invoice_no} already has challan ${inv.challan_number}. Replace it with ${b.bill_no}?`)) return;
    const edit_reason = askReason(user, "linking store bill", `invoice ${inv.invoice_no}`, true);
    if (edit_reason === null) return;
    try {
      const res = await api.post("/store-sync/link", { bill_no: b.bill_no, invoice_id: inv.id, edit_reason });
      if (res.data?.status === "pending") toast.info(res.data.message);
      else toast.success(`Bill ${b.bill_no} linked to ${inv.invoice_no}`);
      await load();
      onLinked();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section data-testid="unlinked-finder" className="rounded-xl border border-destructive/25 bg-card mb-6 animate-fade-up">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border">
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-display text-base md:text-lg font-semibold">Unlinked bill finder</h2>
          <p className="text-xs text-muted-foreground">Store bills with no Tata Motors invoice carrying their challan number. Pick the invoice and tap Link to save the bill number as its challan.</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="finder-search" className={`${ctl} pl-8 w-52`} placeholder="Bill no. or vehicle" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button data-testid="finder-close" onClick={onClose} className="h-9 w-9 rounded-md border border-input flex items-center justify-center hover:bg-muted transition-colors"><X size={15} /></button>
      </div>
      {!data ? <div className="p-6 text-sm text-muted-foreground">Loading…</div> : bills.length === 0 ? (
        <div data-testid="finder-empty" className="p-6 text-sm text-center text-muted-foreground">{data.bills.length ? "No bills match your search." : "Every store bill is linked."}</div>
      ) : (
        <div className="divide-y divide-border max-h-[60vh] overflow-y-auto">
          <div className="hidden lg:grid grid-cols-[120px_100px_130px_110px_minmax(0,1fr)_90px] gap-3 px-4 py-2 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span>Bill no.</span><span>Date</span><span>Vehicle</span><span className="text-right">Amount</span><span>Tata Motors invoice</span><span></span>
          </div>
          {bills.map((b) => <BillRow key={b.bill_no} b={b} cands={data.candidates} byId={byId} canWrite={canWrite} onLink={link} />)}
        </div>
      )}
    </section>
  );
};
